import {IndexedDBCache} from "finhelper";
import {Api} from "laravel-request";
import {NotifyManager} from "notify-component";
import {Subscription, from, timer} from "rxjs";
import {switchMap, take} from "rxjs/operators";

import {getUsersTodayValuation} from "#app/api";
import {NotifyManagerErrors} from "#app/constants/NotifyManagerErrors";

/** Ответ index / Laravel может быть массивом или несколько уровней `{ data: ... }`. */
function unwrapToRows(payload: any): any[] {
    let cur: any = payload;
    for (let d = 0; d < 6; d++) {
        if (Array.isArray(cur)) {
            return cur;
        }
        if (cur && typeof cur === "object" && "data" in (cur as object)) {
            cur = (cur as { data: any }).data;
            continue;
        }
        break;
    }
    return [];
}

/** Совпадает со статистикой: «Мои» → manager_id; фингруппа → без manager_id (scope по Auth). */
export type AtonActivesScope = {
    financeGroupIncluded: boolean;
    managerId: number;
    scopedUserIds: number[];
    accountNumbers?: string[];
    /** CRM PK + platform user_id для WS `aton-portfolio/index` fallback. */
    allowedAtonRowUserIds?: number[];
};

function scopeCacheTag(scope: AtonActivesScope): string {
    const uid = [...scope.scopedUserIds].sort((a, b) => a - b).join(",");
    const acc = [...(scope.accountNumbers ?? [])].sort().join("|");
    const slice = `fg${scope.financeGroupIncluded ? 1 : 0}_u${uid}_a${acc}`;
    return slice.length > 220 ? `${slice.slice(0, 220)}…` : slice;
}

/** Ответ `users_today_valuation`: одна строка на клиента — поля для PseudoActive / сумм. */
function mapUsersTodayValuationToActivesRows(rows: any[]): any[] {
    if (!Array.isArray(rows)) {
        return [];
    }

    return rows.map((raw: any, idx: number) => {
        const uid = raw?.user_id;
        const val = Number(raw?.value ?? 0);
        const id = uid != null ? `utv-${uid}` : `utv-idx-${idx}`;
        return {
            ...raw,
            id,
            score_position_rub: val,
            valuation: val,
            value: val,
            name_text: raw?.name_text ?? "Совокупная оценка портфеля",
            type_text: raw?.type_text ?? "Портфель",
        };
    });
}

const INVEST_GRID_POLL_INTERVAL_MS = 5000;
const INVEST_GRID_MAX_POLL_ATTEMPTS = 72;

type InvestGridRequestPayload = {
    all_users: number;
    exchange_valuation: number;
    job_id: string | null;
    job_date: string | null;
};

function clearInvestGridJobStorage(managerId: string | number): void {
    localStorage.removeItem(`actives_job_id.${managerId}`);
    localStorage.removeItem(`actives_job_date.${managerId}`);
}

function isTerminalInvestGridClientError(statusCode: number): boolean {
    return statusCode >= 400 && statusCode < 500;
}

export class AllActivesService {
    static jobSubscription: Subscription | null = null

    /** Dedupe параллельных invest-grid: один запрос пишет кэш, остальные ждут. */
    static isLoading = false

    static textFormatted = [];

    static chartData = null;

    static series = null;

    private static releaseInvestGridLoading(): void {
        AllActivesService.isLoading = false;
    }

    private static stopInvestGridPolling(): void {
        AllActivesService.jobSubscription?.unsubscribe();
        AllActivesService.jobSubscription = null;
    }

    private static failInvestGridJob(
        managerId: string | number,
        notifyId: string | number | null | undefined,
        reject: (reason?: unknown) => void,
        errorPayload: unknown
    ): void {
        AllActivesService.stopInvestGridPolling();
        AllActivesService.releaseInvestGridLoading();
        clearInvestGridJobStorage(managerId);
        if (notifyId != null) {
            NotifyManager.delete(notifyId);
        }
        reject(errorPayload);
    }

    private static startInvestGridPolling(
        managerId: string | number,
        activeData: InvestGridRequestPayload,
        rememberTime: number,
        notifyId: string | number,
        resolve: (value: unknown) => void,
        reject: (reason?: unknown) => void
    ): void {
        AllActivesService.stopInvestGridPolling();
        let pollAttemptCount = 0;

        AllActivesService.jobSubscription = timer(0, INVEST_GRID_POLL_INTERVAL_MS).subscribe(() => {
            pollAttemptCount += 1;
            if (pollAttemptCount > INVEST_GRID_MAX_POLL_ATTEMPTS) {
                AllActivesService.failInvestGridJob(
                    managerId,
                    notifyId,
                    reject,
                    { status: 408, message: "invest-grid-all-index polling timeout" }
                );
                return;
            }

            const whiteswanApiUrl = process.env.REACT_APP_API_WHITESWAN_URL;
            if (!whiteswanApiUrl) {
                AllActivesService.failInvestGridJob(
                    managerId,
                    notifyId,
                    reject,
                    new Error(NotifyManagerErrors.ENV_PARAMETER_NOT_SET)
                );
                NotifyManager.error("Ошибка", NotifyManagerErrors.ENV_PARAMETER_NOT_SET);
                return;
            }

            Api.get("active", "invest-grid-all-index", activeData)
                .setDomain(whiteswanApiUrl)
                .withoutNotify()
                .call(
                    async (response) => {
                        await IndexedDBCache.set("all_actives", response.data, rememberTime);
                        AllActivesService.stopInvestGridPolling();
                        AllActivesService.releaseInvestGridLoading();
                        NotifyManager.delete(notifyId);
                        resolve(response.data);
                    },
                    (xhr, responseData) => {
                        const statusCode = xhr?.status ?? 0;
                        if (!isTerminalInvestGridClientError(statusCode)) {
                            return;
                        }
                        AllActivesService.failInvestGridJob(managerId, notifyId, reject, {
                            status: statusCode,
                            responseData,
                            message: "invest-grid-all-index failed",
                        });
                    }
                );
        });
    }

    private static requestInvestGridIndex(
        managerId: string | number,
        activeData: InvestGridRequestPayload,
        rememberTime: number,
        notifyId: string | number,
        resolve: (value: unknown) => void,
        reject: (reason?: unknown) => void
    ): void {
        const whiteswanApiUrl = process.env.REACT_APP_API_WHITESWAN_URL;
        if (!whiteswanApiUrl) {
            AllActivesService.releaseInvestGridLoading();
            NotifyManager.error("Ошибка", NotifyManagerErrors.ENV_PARAMETER_NOT_SET);
            reject(new Error(NotifyManagerErrors.ENV_PARAMETER_NOT_SET));
            return;
        }

        Api.get("active", "invest-grid-all-index", activeData)
            .setDomain(whiteswanApiUrl)
            .call(
                async (response) => {
                    if (response.data?.job_id && response.data?.job_date) {
                        localStorage.setItem(
                            `actives_job_id.${managerId}`,
                            String(response.data.job_id)
                        );
                        localStorage.setItem(
                            `actives_job_date.${managerId}`,
                            String(response.data.job_date)
                        );

                        const pollingPayload: InvestGridRequestPayload = {
                            ...activeData,
                            job_id: String(response.data.job_id),
                            job_date: String(response.data.job_date),
                        };
                        AllActivesService.startInvestGridPolling(
                            managerId,
                            pollingPayload,
                            rememberTime,
                            notifyId,
                            resolve,
                            reject
                        );
                        return;
                    }

                    await IndexedDBCache.set("all_actives", response.data, rememberTime);
                    AllActivesService.releaseInvestGridLoading();
                    NotifyManager.delete(notifyId);
                    resolve(response.data);
                },
                (xhr, responseData) => {
                    const statusCode = xhr?.status ?? 0;
                    if (isTerminalInvestGridClientError(statusCode)) {
                        clearInvestGridJobStorage(managerId);
                    }
                    AllActivesService.failInvestGridJob(managerId, notifyId, reject, {
                        status: statusCode,
                        responseData,
                        message: "invest-grid-all-index failed",
                    });
                }
            );
    }

    /**
     * ATON без `atonScope` — только вкладка «Активы» и т.п.: WhiteSwan `aton-portfolio/index` + catalog,
     * без BlackSwan aggregate и без данных статистики/Redux.
     * С `atonScope` — рейтинг воронки: aggregate `users_today_valuation` + fallback WS + фильтр по клиентам.
     */
    static do = async (
        managerId: string | number,
        atonValuation: number,
        atonScope?: AtonActivesScope | null
    ): Promise<any> => {
        if (atonValuation) {
            // Funnel → Активы: без статистики / Redux
            if (!atonScope) {
                const cacheKey = `aton_portfolio_ws_isolated__manager-${managerId}__v-${atonValuation}`;
                const cached = await IndexedDBCache.get(cacheKey);
                if (cached !== undefined && cached !== null) {
                    const rows = Array.isArray(cached) ? cached : unwrapToRows(cached);
                    if (rows.length > 0) {
                        return Promise.resolve(rows);
                    }
                }

                return await new Promise((resolve, reject) => {
                    if (process.env.REACT_APP_API_WHITESWAN_URL) {
                        Api.get('aton-portfolio', 'index')
                            .setDomain(process.env.REACT_APP_API_WHITESWAN_URL as string)
                            .where('manager_id', managerId)
                            .with('catalog')
                            .all(async ({data}: { data: any }) => {
                                const rows = unwrapToRows(data);
                                await IndexedDBCache.set(cacheKey, rows, 1000 * 60 * 15);
                                resolve(rows);
                            });
                    } else {
                        NotifyManager.error('Ошибка', NotifyManagerErrors.ENV_PARAMETER_NOT_SET);
                        reject(new Error("REACT_APP_API_WHITESWAN_URL is not set"));
                    }
                });
            }

            // Рейтинг воронки: aggregate + scope
            const scopePart = scopeCacheTag(atonScope);
            const cacheKey = `aton_actives_utv__m-${managerId}__${scopePart}__v-${atonValuation}`;
            const atonValuationDataCached = await IndexedDBCache.get(cacheKey);

            if (atonValuationDataCached !== undefined && atonValuationDataCached !== null) {
                const rows = Array.isArray(atonValuationDataCached)
                    ? atonValuationDataCached
                    : unwrapToRows(atonValuationDataCached);
                if (rows.length > 0) {
                    return Promise.resolve(rows);
                }
            }

            let rows: any[] = [];

            const hasScopeFilter =
                (atonScope.scopedUserIds?.length ?? 0) > 0 ||
                (atonScope.accountNumbers?.length ?? 0) > 0;

            const mgrForBs = atonScope.financeGroupIncluded && hasScopeFilter
                ? undefined
                : Number(managerId);
            const userIdsPayload =
                atonScope.scopedUserIds && atonScope.scopedUserIds.length > 0
                    ? atonScope.scopedUserIds
                    : undefined;
            const accountsPayload =
                atonScope.accountNumbers && atonScope.accountNumbers.length > 0
                    ? atonScope.accountNumbers
                    : undefined;

            if (process.env.REACT_APP_API_URL) {
                rows = await new Promise<any[]>((resolve) => {
                    getUsersTodayValuation(mgrForBs as any, userIdsPayload as any, accountsPayload as any).all(
                        (response: { data?: any } | unknown[]) => {
                            const payload = Array.isArray(response) ? response : response?.data;
                            const arr = Array.isArray(payload) ? payload : unwrapToRows(payload);
                            resolve(mapUsersTodayValuationToActivesRows(arr));
                        },
                        () => resolve([])
                    );
                });
            }

            if (rows.length === 0 && process.env.REACT_APP_API_WHITESWAN_URL) {
                rows = await new Promise<any[]>((resolve) => {
                    Api.get('aton-portfolio', 'index')
                        .setDomain(process.env.REACT_APP_API_WHITESWAN_URL as string)
                        .where('manager_id', managerId)
                        .with('catalog')
                        .all(
                            async ({data}: { data: any }) => {
                                let r = unwrapToRows(data);
                                const filterIds =
                                    atonScope.allowedAtonRowUserIds?.length
                                        ? atonScope.allowedAtonRowUserIds
                                        : atonScope.scopedUserIds;
                                if (filterIds && filterIds.length > 0) {
                                    const allowed = new Set(filterIds.map(Number));
                                    r = r.filter((row: any) => allowed.has(Number(row?.user_id)));
                                }
                                resolve(r);
                            },
                            () => resolve([])
                        );
                });
            }

            if (!process.env.REACT_APP_API_URL && !process.env.REACT_APP_API_WHITESWAN_URL) {
                NotifyManager.error('Ошибка', NotifyManagerErrors.ENV_PARAMETER_NOT_SET);
                throw new Error("REACT_APP_API_URL or REACT_APP_API_WHITESWAN_URL required for ATON rating scope");
            }

            if (rows.length > 0) {
                await IndexedDBCache.set(cacheKey, rows, 1000 * 60 * 15);
            }

            return Promise.resolve(rows);

        } else {
            // TTL кэша invest-grid: 24 часа
            const rememberTime = 1000 * 60 * 60 * 24;

            return new Promise((resolve, reject) => {
                void (async () => {
                    try {
                        const data = await IndexedDBCache.get('all_actives')

                        if (!data && !this.isLoading) {
                            this.isLoading = true;

                            const id = NotifyManager.loading('', 'Загрузка данных')

                            const activeData: InvestGridRequestPayload = {
                                all_users: 1,
                                exchange_valuation: 1,
                                job_id: localStorage.getItem(`actives_job_id.${managerId}`),
                                job_date: localStorage.getItem(`actives_job_date.${managerId}`),
                            };

                            if (activeData.job_id) {
                                AllActivesService.startInvestGridPolling(
                                    managerId,
                                    activeData,
                                    rememberTime,
                                    id,
                                    resolve,
                                    reject
                                );
                            } else {
                                AllActivesService.requestInvestGridIndex(
                                    managerId,
                                    activeData,
                                    rememberTime,
                                    id,
                                    resolve,
                                    reject
                                );
                            }
                        } else if (this.isLoading) {
                            AllActivesService.stopInvestGridPolling();
                            let cacheWaitResolved = false;
                            AllActivesService.jobSubscription = timer(0, 1000)
                                .pipe(
                                    switchMap(() => from(IndexedDBCache.get("all_actives"))),
                                    take(INVEST_GRID_MAX_POLL_ATTEMPTS),
                                )
                                .subscribe({
                                    next: (cachedActives) => {
                                        if (cacheWaitResolved || !cachedActives) {
                                            return;
                                        }
                                        cacheWaitResolved = true;
                                        AllActivesService.stopInvestGridPolling();
                                        resolve(cachedActives);
                                    },
                                    complete: () => {
                                        if (!cacheWaitResolved) {
                                            AllActivesService.failInvestGridJob(
                                                managerId,
                                                null,
                                                reject,
                                                {
                                                    status: 408,
                                                    message: "invest-grid-all-index cache wait timeout",
                                                }
                                            );
                                        }
                                    },
                                });
                        } else {
                            // Кэш: в норме массив позиций; иногда попадает объект-заглушка job — не зависаем на Promise.
                            if (Array.isArray(data)) {
                                resolve(data);
                                return;
                            }
                            if (
                                data &&
                                typeof data === "object" &&
                                !Array.isArray(data) &&
                                Object.prototype.hasOwnProperty.call(data, "job_id") &&
                                (data as { job_id?: any }).job_id === null
                            ) {
                                await AllActivesService.clear();
                                resolve([]);
                                return;
                            }
                            resolve([]);
                        }
                    } catch (e) {
                        reject(e);
                    }
                })();
            });
        }
    }

    static async clear(
        managerId?: string | number,
        atonValuation?: number
    ) {
        this.isLoading = false;
        this.jobSubscription?.unsubscribe();
        this.jobSubscription = null;
        this.chartData = null;
        this.series = null;
        await IndexedDBCache.delete('all_actives');
        if (managerId != null && atonValuation != null) {
            await IndexedDBCache.delete(
                `aton_portfolio_ws_isolated__manager-${managerId}__v-${atonValuation}`
            );
        }
    }
}


